import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBreedsByPetName } from '@/app/actions';
import debounce from 'lodash.debounce';
import { ChevronDown24Regular } from '@fluentui/react-icons';

const SearchSelectBreed: React.FC<{ petType: string; setBreed: (breed: string) => void }> = ({ petType, setBreed }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedValue, setSelectedValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, error } = useQuery({
    queryKey: ['breeds', petType, searchTerm],
    queryFn: () => getBreedsByPetName(petType, 1, 40),
    enabled: searchTerm.length >= 0,
  });

  useEffect(() => {
    const filteredSuggestions =
      data?.items
        .map((item: any) => item.friendlyName || item.name)
        .filter((suggestion: string) =>
          suggestion.toLowerCase().startsWith(searchTerm.toLowerCase())
        ) || [];
    setSuggestions(filteredSuggestions);

    if (filteredSuggestions.length > 0 && searchTerm.length > 0) {
      setShowSuggestions(true);
      setSelectedValue(filteredSuggestions[0]);
    } else {
      setShowSuggestions(false);
      setSelectedValue('');
    }
  }, [data, searchTerm]);

  useEffect(() => {
    setSearchTerm('');
    setDisplayValue('');
    setShowSuggestions(false);
    setSelectedValue('');
  }, [petType]);

  const handleSearchChange = debounce((value: string) => {
    setSearchTerm(value);
  }, 100);

  const handleBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
      if (selectedValue && displayValue.toLowerCase() === searchTerm.toLowerCase()) {
        setDisplayValue(selectedValue);
        setBreed(selectedValue);
      }
    }, 100);
  };

  const handleInputChange = (value: string) => {
    setDisplayValue(value);
    handleSearchChange(value);
    if (!value) {
      setBreed('');
    }
  };

  return (
    <div className="flex flex-col relative">
      <div className="relative w-full">
        <input
          type="text"
          disabled={!petType}
          placeholder="Search for breed"
          value={displayValue}
          ref={inputRef}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
          onBlur={handleBlur}
          className="input input-bordered w-full border-primary pr-10 disabled:bg-base-content/10 z-10 relative"
        />
        <ChevronDown24Regular className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none" />
        {selectedValue && displayValue.toLowerCase() !== selectedValue.toLowerCase() && searchTerm.length > 0 && (
          <input
            type="text"
            value={selectedValue}
            className="absolute top-0 left-0 w-full h-full input input-bordered bg-transparent text-gray-400 pointer-events-none"
            readOnly
          />
        )}
      </div>
      {error && <div className="text-error">Error fetching breeds</div>}
      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute bg-white border border-gray-300 w-full max-h-40 overflow-y-auto z-20 mt-1 top-12">
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              className="p-2 cursor-pointer hover:bg-gray-200"
              onMouseDown={() => {
                setBreed(suggestion);
                setDisplayValue(suggestion);
                setSelectedValue(suggestion);
                setSuggestions([]);
                setShowSuggestions(false);
              }}
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchSelectBreed;